/**
 * ShipmentsGridPreview — the full-width shipments list view (/preview/shipments).
 *
 * Split out of the workspace so the workspace can use the full width for the
 * single-shipment view. This is the list surface: one row per shipment, search
 * + status filter + clickable column sort. Clicking a row opens that shipment
 * in the workspace (/preview/workspace?id=…).
 *
 * Columns follow the freight domain — ID · Route · Mode · Status · Activity —
 * and reuse the same status vocabulary as the rest of the app (STATUS_LABELS).
 * Preview-only: data comes from INITIAL_SHIPMENTS, no API.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { NavSidebar } from "./Sidebar";
import { INITIAL_SHIPMENTS, STATUS_LABELS } from "./mockData";
import { displayName } from "./helpers";
import "../V2Workspace.css";

const STATUS_CLASS = {
	DRAFT: "is-draft",
	RFQS_SENT: "is-rfqs",
	DECIDE: "is-decide",
	BOOKED: "is-booked",
};

// Lifecycle order — drives the Status-column sort and the filter dropdown.
const STATUS_ORDER = ["DRAFT", "RFQS_SENT", "DECIDE", "BOOKED"];

function lastActivity(s) {
	const tl = s.timeline || [];
	const cur = tl.find((t) => t.current) || tl[tl.length - 1];
	return cur ? { label: cur.label, date: cur.date || "" } : { label: "—", date: "" };
}

function routeOf(s) {
	const o = s.route?.origin || {};
	const d = s.route?.destination || {};
	return {
		from: o.name || o.code || "—",
		to: d.name || d.code || "—",
		fromCode: o.code || "",
		toCode: d.code || "",
	};
}

const SearchIcon = () => (
	<svg
		width="15"
		height="15"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<circle cx="11" cy="11" r="7" />
		<line x1="21" y1="21" x2="16.65" y2="16.65" />
	</svg>
);

export default function ShipmentsGridPreview() {
	const navigate = useNavigate();
	const [navCollapsed, setNavCollapsed] = useState(false);
	const [query, setQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("ALL");
	const [sort, setSort] = useState({ key: "id", dir: "desc" });

	const rows = useMemo(() => {
		let list = INITIAL_SHIPMENTS.map((s) => {
			const route = routeOf(s);
			return {
				id: s.id,
				name: displayName(s, INITIAL_SHIPMENTS),
				route,
				routeKey: `${route.from} ${route.to} ${route.fromCode} ${route.toCode}`,
				mode: s.mode || "—",
				status: s.status,
				statusLabel: STATUS_LABELS[s.status] || s.status,
				activity: lastActivity(s),
				createdAt: s.createdAt || "",
				needsAttention: !!s.needsAttention,
			};
		});

		const q = query.trim().toLowerCase();
		if (q) {
			list = list.filter((r) =>
				[r.id, r.name, r.routeKey, r.mode, r.statusLabel]
					.join(" ")
					.toLowerCase()
					.includes(q),
			);
		}
		if (statusFilter !== "ALL") {
			list = list.filter((r) => r.status === statusFilter);
		}

		const dir = sort.dir === "asc" ? 1 : -1;
		list.sort((a, b) => {
			let av;
			let bv;
			switch (sort.key) {
				case "route":
					av = a.route.from;
					bv = b.route.from;
					break;
				case "mode":
					av = a.mode;
					bv = b.mode;
					break;
				case "status":
					av = STATUS_ORDER.indexOf(a.status);
					bv = STATUS_ORDER.indexOf(b.status);
					break;
				case "activity":
					av = a.createdAt;
					bv = b.createdAt;
					break;
				default:
					av = a.id;
					bv = b.id;
			}
			if (av < bv) return -1 * dir;
			if (av > bv) return 1 * dir;
			return 0;
		});
		return list;
	}, [query, statusFilter, sort]);

	const toggleSort = (key) =>
		setSort((prev) =>
			prev.key === key
				? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
				: { key, dir: "asc" },
		);
	const caret = (key) =>
		sort.key === key ? (sort.dir === "asc" ? " ↑" : " ↓") : "";

	const statuses = [
		"ALL",
		...STATUS_ORDER.filter((s) => INITIAL_SHIPMENTS.some((sh) => sh.status === s)),
	];

	return (
		<div className="ws-app">
			<NavSidebar
				collapsed={navCollapsed}
				onToggle={() => setNavCollapsed((c) => !c)}
			/>
			<div className="ws-shipgrid">
				<div className="ws-shipgrid-topbar">
					<span className="ws-shipgrid-breadcrumb">Shipments</span>
					<span className="ws-shipgrid-lens">Preview · grid view</span>
				</div>

				<div className="ws-shipgrid-head">
					<div>
						<div className="ws-shipgrid-eyebrow">Grid view</div>
						<h1 className="ws-shipgrid-title">
							All shipments · sortable, filterable
						</h1>
					</div>
					<button
						type="button"
						className="ws-btn ws-btn--primary"
						onClick={() => navigate("/preview/workspace")}
					>
						<Plus size={15} strokeWidth={2.2} /> New shipment
					</button>
				</div>

				<div className="ws-shipgrid-toolbar">
					<div className="ws-shipgrid-search">
						<SearchIcon />
						<input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search shipments…"
							aria-label="Search shipments"
						/>
					</div>
					<select
						className="ws-shipgrid-filter"
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value)}
						aria-label="Filter by status"
					>
						{statuses.map((s) => (
							<option key={s} value={s}>
								{s === "ALL" ? "Status: all" : `Status: ${STATUS_LABELS[s] || s}`}
							</option>
						))}
					</select>
					<div className="ws-shipgrid-toggle">
						<button type="button" className="is-active">
							Grid
						</button>
						<button type="button" disabled title="Coming soon">
							Pipeline
						</button>
					</div>
				</div>

				<div className="ws-shipgrid-tablewrap">
					<table className="ws-shipgrid-table">
						<thead>
							<tr>
								<th onClick={() => toggleSort("id")}>ID{caret("id")}</th>
								<th onClick={() => toggleSort("route")}>Route{caret("route")}</th>
								<th onClick={() => toggleSort("mode")}>Mode{caret("mode")}</th>
								<th onClick={() => toggleSort("status")}>
									Status{caret("status")}
								</th>
								<th onClick={() => toggleSort("activity")}>
									Activity{caret("activity")}
								</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => (
								<tr
									key={r.id}
									onClick={() => navigate(`/preview/workspace?id=${r.id}`)}
								>
									<td className="ws-shipgrid-id">
										<span className="ws-shipgrid-id-row">
											{r.needsAttention && <span className="ws-dot-attention" />}
											{r.id}
										</span>
										<span className="ws-shipgrid-id-name">{r.name}</span>
									</td>
									<td>
										<span className="ws-shipgrid-route">
											{r.route.from} → {r.route.to}
										</span>
										<span className="ws-shipgrid-route-code">
											{r.route.fromCode} → {r.route.toCode}
										</span>
									</td>
									<td>{r.mode}</td>
									<td>
										<span
											className={`ws-shipgrid-status ${STATUS_CLASS[r.status] || ""}`}
										>
											{r.statusLabel}
										</span>
									</td>
									<td className="ws-shipgrid-activity">
										<span>{r.activity.label}</span>
										{r.activity.date && (
											<span className="ws-shipgrid-activity-date">
												{r.activity.date}
											</span>
										)}
									</td>
								</tr>
							))}
							{rows.length === 0 && (
								<tr>
									<td colSpan={5} className="ws-shipgrid-empty">
										No shipments match your filters.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}
